import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import { reviewsIconCollectionManagerToken } from "@apps/reviews/tokens";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { I18nModule } from "@modules/i18n/I18nModule";
import { SvelteComponent } from "svelte";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ListingAverageReviewsWidgetComponent from "./ListingAverageReviewsWidget.svelte";
import { ListingAverageReviewsWidgetConfigInterface } from "./ListingAverageReviewsWidgetConfig";
import ListingProductInfoQuery from "./queries/ListingProductInfoQuery";
import ListingProductInfoService from "./services/ListingProductInfoService";

@injectable()
class ListingAverageReviewsWidget {
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private component: SvelteComponent | undefined = undefined;
    private readonly i18nModule: I18nModule;

    constructor(
        private readonly config: ListingAverageReviewsWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly productIdManager: ProductIdManagerInterface
    ) {
        this.logger.debug("ListingAverageReviewsWidget config", this.config);

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        // I18n
        this.i18nModule = container.resolve(I18nModule);
        this.i18nModule.exports.i18n.registerLoader(
            "reviews.AverageReviewsWidget",
            this.i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.AverageReviewsWidget"
            )
        );
    }

    async init(): Promise<this> {
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const listingProductInfoService = container.resolve(
            ListingProductInfoService
        );
        const listingProductInfoQuery = container.resolve(
            ListingProductInfoQuery
        );

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromDesignMode({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );

        this.component = new ListingAverageReviewsWidgetComponent({
            target: shadowRoot || element,
            props: {
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                listingProductInfoService,
                listingProductInfoQuery,
                productIdManager: this.productIdManager,
                showReviewsTooltip: this.config.showReviewsTooltip,
                gwI18n: this.i18nModule.exports.i18n,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
            context: contextMap,
        });

        return this;
    }

    destroy() {
        if (this.component) {
            this.component.$destroy();
        }
    }
}

export default ListingAverageReviewsWidget;
